// Absenteeism Widget — rolling-12-week sick-day rate per department.
//
// Pairs every scheduled person-day in the rolling window with any sick
// override for the same (date, employee) and surfaces:
//   • One headline "rolling N-week sick rate" + a sparkline of the
//     weekly trend so clusters jump out.
//   • A per-department breakdown sorted by rate descending, with the
//     worst department flagged amber/red so it doesn't get lost in the
//     middle of the list.
//
// Backend: GET /api/planning/absenteeism?weeks=12
//   Returns overall + by_department + trend (one entry per ISO week).
//   "Sick days" only counts entries that pair with a scheduled day —
//   unscheduled sick rows are ignored so they can't inflate the rate.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Activity, ArrowRight, AlertTriangle } from 'lucide-react';
import { BRAND } from '.';
import { safeFetchJson } from '@/utils/safeFetch';

const API = process.env.REACT_APP_BACKEND_URL;

// Thresholds for the colour-coded headline + per-row pills. Tuned to
// the typical 4-6 % industry rate so a 7 % week already pulses amber.
const RATE_GREEN = 4;
const RATE_AMBER = 7;

const rateColor = (pct) => {
  if (pct == null) return '#94a3b8';
  if (pct < RATE_GREEN) return '#10b981'; // green
  if (pct < RATE_AMBER) return '#f59e0b'; // amber
  return '#ef4444'; // red
};

// Tiny dependency-free sparkline (uses one inline SVG).
const Sparkline = ({ points, width = 220, height = 38 }) => {
  const valid = points.filter((p) => p.rate_pct != null);
  if (valid.length === 0) {
    return <div className="text-[11px] text-muted-foreground">No sick data in this window.</div>;
  }
  const max = Math.max(...valid.map((p) => p.rate_pct), 5);
  const stepX = width / Math.max(points.length - 1, 1);
  // The first *plotted* point must be a moveto — a leading gap in the
  // series would otherwise emit an invalid path starting with "L".
  let started = false;
  const path = points
    .map((p, i) => {
      if (p.rate_pct == null) return null;
      const x = i * stepX;
      const y = height - (p.rate_pct / max) * (height - 4) - 2;
      const cmd = started ? 'L' : 'M';
      started = true;
      return `${cmd} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .filter(Boolean)
    .join(' ');
  const last = valid[valid.length - 1];
  const lastIdx = points.lastIndexOf(last);
  const lastX = lastIdx * stepX;
  const lastY = height - (last.rate_pct / max) * (height - 4) - 2;
  return (
    <svg width={width} height={height} aria-label="Weekly sick-rate trend">
      {/* Reference 4 % baseline so the user can spot drift visually. */}
      <line
        x1={0} x2={width}
        y1={height - (RATE_GREEN / max) * (height - 4) - 2}
        y2={height - (RATE_GREEN / max) * (height - 4) - 2}
        stroke="#cbd5e1" strokeDasharray="2,3" strokeWidth="1"
      />
      <path d={path} stroke={rateColor(last.rate_pct)} strokeWidth="1.6" fill="none"
            strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lastX} cy={lastY} r="2.5" fill={rateColor(last.rate_pct)} />
    </svg>
  );
};

export const AbsenteeismWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weeks, setWeeks] = useState(() => {
    try { return Number(localStorage.getItem('absenteeism.weeks')) || 12; }
    catch { return 12; }
  });

  useEffect(() => {
    try { localStorage.setItem('absenteeism.weeks', String(weeks)); } catch { /* ignore */ }
  }, [weeks]);

  useEffect(() => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (!token) { setLoading(false); return; }
    setLoading(true);
    safeFetchJson(`${API}/api/planning/absenteeism?weeks=${weeks}`, { token })
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [weeks]);

  const overallPct = data?.overall?.rate_pct;
  const overallColor = rateColor(overallPct);
  const worstDept = data?.by_department?.[0];

  return (
    <Card data-testid="absenteeism-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5" style={{ color: BRAND.cyan }} />
              Absenteeism — rolling sick rate
            </CardTitle>
            <CardDescription>
              {data
                ? `Last ${data.weeks} weeks (${data.from_week} → ${data.to_week}). Rate = sick person-days ÷ scheduled person-days.`
                : 'Pairs every scheduled person-day with sick overrides.'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="absenteeism-weeks"
              value={weeks}
              onChange={(e) => setWeeks(Number(e.target.value))}
              className="text-xs border rounded px-2 py-1 bg-white"
            >
              {[4, 8, 12, 26, 52].map((w) => (
                <option key={w} value={w}>{w} weeks</option>
              ))}
            </select>
            <Link to="/planning" className="text-xs flex items-center gap-1 text-[#2D8C8C] hover:underline">
              Open planning <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {loading && <p className="text-xs text-muted-foreground">Loading absenteeism…</p>}

        {!loading && (!data || data.overall.scheduled_days === 0) && (
          <p className="text-xs text-muted-foreground">
            No scheduled person-days in this window yet.
          </p>
        )}

        {!loading && data && data.overall.scheduled_days > 0 && (
          <>
            <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-center">
              <div>
                <div className="flex items-baseline gap-2">
                  <span
                    className="text-3xl font-bold tabular-nums"
                    style={{ color: overallColor }}
                    data-testid="absenteeism-overall"
                  >
                    {overallPct != null ? `${overallPct.toFixed(1)}%` : '—'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {data.overall.sick_days} sick / {data.overall.scheduled_days} scheduled
                  </span>
                </div>
                {worstDept && worstDept.rate_pct != null && worstDept.rate_pct >= RATE_AMBER && (
                  <p className="text-[11px] text-rose-700 flex items-center gap-1 mt-1">
                    <AlertTriangle className="h-3 w-3" />
                    Hot spot: <span className="font-medium">{worstDept.department_name}</span> at {worstDept.rate_pct.toFixed(1)}%
                  </p>
                )}
              </div>
              <div className="sm:justify-self-end">
                <Sparkline points={data.trend} />
              </div>
            </div>

            <div className="space-y-1.5">
              {data.by_department.map((d) => {
                const pct = d.rate_pct ?? 0;
                const color = rateColor(d.rate_pct);
                // Cap the bar fill at 15 % for visual purposes — beyond
                // that a longer bar adds no information, only horror.
                const barWidth = Math.min(pct / 15 * 100, 100);
                return (
                  <div key={d.department_id} className="grid grid-cols-[140px_1fr_auto] gap-2 items-center text-xs">
                    <span className="truncate" title={d.department_name}>{d.department_name}</span>
                    <div className="h-2 bg-muted/40 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${barWidth}%`, backgroundColor: color }}
                      />
                    </div>
                    <span className="tabular-nums font-medium" style={{ color, minWidth: '80px', textAlign: 'right' }}>
                      {d.rate_pct != null ? `${d.rate_pct.toFixed(1)}%` : '—'}
                      <span className="text-muted-foreground font-normal ml-1">
                        ({d.sick_days}/{d.scheduled_days})
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
